if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.variantIdInput = this.form.querySelector('[name="id"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.submitButtonText = this.submitButton
          ? this.submitButton.querySelector('[data-add-to-cart-label], span:not(.sold-out-message):not([data-in-cart-controls])')
          : null;
        this.submitButtonSoldOutMessage = this.submitButton ? this.submitButton.querySelector('.sold-out-message') : null;
        this.inCartControls = this.submitButton ? this.submitButton.querySelector('[data-in-cart-controls]') : null;
        this.inCartCountInput = this.submitButton ? this.submitButton.querySelector('[data-cart-count-input]') : null;
        this.inCartRemoveButton = this.querySelector('[data-remove-from-cart]');

        this.defaultButtonStyle = this.submitButton?.dataset.defaultButtonStyle || 'button--primary';
        this.currentCartState = null;
        this.currentVariantLineItem = null;
        this.currentVariantQuantity = 0;
        this.pendingVariantQuantities = {};
        this.isUpdatingQuantity = false;
        this.quantityTypingTimeout = null;

        this.onSubmitButtonClick = this.onSubmitButtonClick.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onQuantityInput = this.onQuantityInput.bind(this);
        this.onQuantityInputBlur = this.onQuantityInputBlur.bind(this);
        this.onQuantityInputKeydown = this.onQuantityInputKeydown.bind(this);
        this.onQuantityInputClick = this.onQuantityInputClick.bind(this);
        this.onRemoveFromCartClick = this.onRemoveFromCartClick.bind(this);
      }

      connectedCallback() {
        if (this.submitButton) {
          this.submitButton.addEventListener('click', this.onSubmitButtonClick);
        }
        if (this.inCartCountInput) {
          this.inCartCountInput.addEventListener('input', this.onQuantityInput);
          this.inCartCountInput.addEventListener('blur', this.onQuantityInputBlur);
          this.inCartCountInput.addEventListener('keydown', this.onQuantityInputKeydown);
          this.inCartCountInput.addEventListener('click', this.onQuantityInputClick);
        }
        if (this.inCartRemoveButton) {
          this.inCartRemoveButton.addEventListener('click', this.onRemoveFromCartClick);
        }

        if (typeof subscribe === 'function') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (this.isFullCartState(event?.cartData)) {
              this.currentCartState = event.cartData;
              this.updateInCartButtonState();
              return;
            }

            this.refreshCartState();
          });
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const eventSectionId = event?.sectionId;
            if (!eventSectionId || eventSectionId === this.dataset.sectionId) {
              this.onVariantChange();
            }
          });
        }

        if (this.variantIdInput) {
          this.variantIdInput.addEventListener('change', this.onVariantChange);
        }

        this.refreshCartState();
      }

      disconnectedCallback() {
        if (this.submitButton) {
          this.submitButton.removeEventListener('click', this.onSubmitButtonClick);
        }
        if (this.inCartCountInput) {
          this.inCartCountInput.removeEventListener('input', this.onQuantityInput);
          this.inCartCountInput.removeEventListener('blur', this.onQuantityInputBlur);
          this.inCartCountInput.removeEventListener('keydown', this.onQuantityInputKeydown);
          this.inCartCountInput.removeEventListener('click', this.onQuantityInputClick);
        }
        if (this.inCartRemoveButton) {
          this.inCartRemoveButton.removeEventListener('click', this.onRemoveFromCartClick);
        }

        if (this.variantIdInput) {
          this.variantIdInput.removeEventListener('change', this.onVariantChange);
        }

        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      onVariantChange() {
        this.updateInCartButtonState();
      }

      onSubmitButtonClick(event) {
        if (!this.isInCartState()) return;

        event.preventDefault();
        event.stopPropagation();

        const decrementTarget = event.target.closest('[data-quantity-decrement]');
        const incrementTarget = event.target.closest('[data-quantity-increment]');

        if (!decrementTarget && !incrementTarget) return;

        const nextQuantity = Math.max(this.currentVariantQuantity + (incrementTarget ? 1 : -1), 0);
        this.changeCartQuantity(nextQuantity);
      }

      onRemoveFromCartClick(event) {
        if (!this.isInCartState()) return;
        event.preventDefault();
        event.stopPropagation();
        this.changeCartQuantity(0);
      }

      onQuantityInput() {
        if (!this.isInCartState() || !this.inCartCountInput) return;
        if (this.quantityTypingTimeout) clearTimeout(this.quantityTypingTimeout);

        this.quantityTypingTimeout = setTimeout(() => {
          this.commitTypedQuantity();
        }, 700);
      }

      onQuantityInputBlur() {
        this.commitTypedQuantity();
      }

      onQuantityInputKeydown(event) {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        this.commitTypedQuantity();
      }

      onQuantityInputClick(event) {
        event.stopPropagation();
      }

      commitTypedQuantity() {
        if (!this.isInCartState() || !this.inCartCountInput) return;
        if (this.quantityTypingTimeout) {
          clearTimeout(this.quantityTypingTimeout);
          this.quantityTypingTimeout = null;
        }

        const parsedQuantity = Number.parseInt(this.inCartCountInput.value, 10);
        const nextQuantity = Number.isFinite(parsedQuantity) ? Math.max(parsedQuantity, 0) : this.currentVariantQuantity;

        if (nextQuantity === this.currentVariantQuantity) {
          this.inCartCountInput.value = `${this.currentVariantQuantity}`;
          return;
        }

        this.changeCartQuantity(nextQuantity);
      }

      async refreshCartState() {
        try {
          const response = await fetch(`${routes.cart_url}.js`);
          if (!response.ok) return;
          const cartState = await response.json();
          this.currentCartState = cartState;
          this.updateInCartButtonState();
        } catch (error) {
          console.error(error);
        }
      }

      getCurrentVariantId() {
        if (!this.variantIdInput) return null;
        const variantId = Number(this.variantIdInput.value);
        return Number.isFinite(variantId) ? variantId : null;
      }

      getPendingVariantQuantity(variantId) {
        if (!variantId) return 0;
        const pendingQuantity = Number(this.pendingVariantQuantities[variantId]);
        return Number.isFinite(pendingQuantity) ? Math.max(pendingQuantity, 0) : 0;
      }

      getCurrentVariantLineItem() {
        if (!this.currentCartState?.items) return null;
        const variantId = this.getCurrentVariantId();
        if (!variantId) return null;

        return (
          this.currentCartState.items.find((item) => Number(item.variant_id) === variantId) ||
          this.currentCartState.items.find((item) => Number(item.id) === variantId) ||
          null
        );
      }

      isInCartState() {
        return this.submitButton?.classList.contains('product-form__submit--in-cart') || false;
      }

      updateInCartButtonState() {
        if (!this.submitButton || !this.inCartControls || !this.inCartCountInput || !this.submitButtonText) return;

        const variantId = this.getCurrentVariantId();
        this.currentVariantLineItem = this.getCurrentVariantLineItem();
        const cartQuantity = this.currentVariantLineItem?.quantity || 0;
        const pendingQuantity = this.getPendingVariantQuantity(variantId);
        this.currentVariantQuantity = Math.max(cartQuantity, pendingQuantity);

        if (variantId && cartQuantity > 0 && this.pendingVariantQuantities[variantId] !== undefined) {
          delete this.pendingVariantQuantities[variantId];
        }

        const isUnavailable = this.submitButton.hasAttribute('disabled');
        const shouldShowInCart = this.currentVariantQuantity > 0 && !isUnavailable;

        this.submitButton.classList.remove('button--primary', 'button--secondary', 'product-form__submit--in-cart');
        if (shouldShowInCart) {
          this.submitButton.classList.add('button--secondary', 'product-form__submit--in-cart');
          this.submitButton.type = 'button';
          this.submitButtonText.classList.add('hidden');
          if (this.submitButtonSoldOutMessage) this.submitButtonSoldOutMessage.classList.add('hidden');
          this.inCartControls.classList.remove('hidden');
          if (this.inCartRemoveButton) this.inCartRemoveButton.classList.remove('hidden');
          this.inCartCountInput.value = `${this.currentVariantQuantity}`;
          this.submitButton.setAttribute(
            'aria-label',
            `Quantity in cart: ${this.currentVariantQuantity}. Use minus and plus controls to adjust quantity.`
          );
          return;
        }

        this.submitButton.classList.add(this.defaultButtonStyle);
        this.submitButton.type = 'submit';
        this.inCartControls.classList.add('hidden');
        if (this.inCartRemoveButton) this.inCartRemoveButton.classList.add('hidden');
        this.inCartCountInput.value = '0';
        this.submitButtonText.classList.remove('hidden');
        this.restoreSubmitText();
        this.submitButton.removeAttribute('aria-label');
      }

      async changeCartQuantity(quantity) {
        if (this.isUpdatingQuantity) return;

        const variantId = this.getCurrentVariantId();
        if (!variantId) return;

        const previousQuantity = this.currentVariantQuantity;
        const shouldCleanupAddonDependencies = quantity < previousQuantity;
        const changedProductId = Number(this.currentVariantLineItem?.product_id);

        this.isUpdatingQuantity = true;
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        try {
          const requestBody = { ...this.getSectionsPayload() };
          let endpoint = routes.cart_change_url;

          if (this.currentVariantLineItem?.key) {
            requestBody.id = this.currentVariantLineItem.key;
            requestBody.quantity = quantity;
          } else {
            endpoint = routes.cart_add_url;
            requestBody.id = variantId;
            requestBody.quantity = quantity;
          }

          const response = await fetch(endpoint, {
            ...fetchConfig('javascript'),
            body: JSON.stringify(requestBody),
          });
          const parsedResponse = await response.json();

          if (parsedResponse.status) {
            this.handleErrorMessage(parsedResponse.description || parsedResponse.message);
            return;
          }

          if (this.isFullCartState(parsedResponse)) {
            this.currentCartState = parsedResponse;
            this.updateInCartButtonState();
          } else {
            await this.refreshCartState();
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: variantId,
            cartData: parsedResponse,
            shouldCleanupAddonDependencies,
            changedProductId: Number.isFinite(changedProductId) ? changedProductId : null,
          });

          if (this.cart) {
            this.renderCartContents(parsedResponse);
          }
        } catch (error) {
          console.error(error);
        } finally {
          this.isUpdatingQuantity = false;
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner')?.classList.add('hidden');
        }
      }

      isFullCartState(cartState) {
        return (
          Array.isArray(cartState?.items) &&
          Number.isFinite(Number(cartState.item_count)) &&
          Number.isFinite(Number(cartState.total_price))
        );
      }

      getSectionsPayload() {
        if (!this.cart) return {};

        return {
          sections: this.cart.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        };
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');
        this.setSubmittingText();

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (this.isFullCartState(response)) {
              this.currentCartState = response;
            }
            const addedVariantId = Number(formData.get('id'));
            if (addedVariantId && !Array.isArray(response.items)) {
              const formQuantity = Number.parseInt(formData.get('quantity'), 10);
              const addQuantity = Number.isFinite(formQuantity) && formQuantity > 0 ? formQuantity : 1;
              this.pendingVariantQuantities[addedVariantId] = Math.max(this.currentVariantQuantity + addQuantity, addQuantity);
            }
            this.updateInCartButtonState();

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.renderCartContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.renderCartContents(response);
            }
            this.triggerAddToCartFeedback();
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            if (!this.error) this.restoreSubmitText();
          });
      }

      renderCartContents(response) {
        if (this.cart && this.cart.matches('cart-drawer')) {
          this.cart.renderContents(response, { open: false });
          return;
        }

        this.cart.renderContents(response);
      }

      triggerAddToCartFeedback() {
        this.revealStickyHeader();
        this.wobbleCartIcon();
      }

      revealStickyHeader() {
        const stickyHeader = document.querySelector('.shopify-section-header-sticky');
        if (stickyHeader && stickyHeader.classList.contains('shopify-section-header-hidden')) {
          stickyHeader.classList.remove('shopify-section-header-hidden');
        }
      }

      wobbleCartIcon() {
        const cartIcon = document.querySelector('.header__icon--cart');
        if (!cartIcon) return;
        cartIcon.classList.remove('is-wobbling');
        void cartIcon.offsetWidth;
        cartIcon.classList.add('is-wobbling');
        cartIcon.addEventListener(
          'animationend',
          () => {
            cartIcon.classList.remove('is-wobbling');
          },
          { once: true }
        );
      }

      setSubmittingText() {
        if (!this.submitButtonText) return;
        if (!this.submitButtonText.dataset.originalText) {
          this.submitButtonText.dataset.originalText = this.submitButtonText.textContent;
        }
        this.submitButtonText.classList.remove('hidden');
        if (this.submitButtonSoldOutMessage) {
          this.submitButtonSoldOutMessage.classList.add('hidden');
        }
        this.submitButtonText.textContent = 'adding...';
      }

      restoreSubmitText() {
        if (!this.submitButtonText || this.isInCartState()) return;
        const originalText = this.submitButtonText.dataset.originalText;
        if (originalText && !this.submitButtonText.classList.contains('hidden')) {
          this.submitButtonText.textContent = originalText;
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
